import { z } from 'zod';

// ─── Audit Actions ──────────────────────────────────────────────────
// Single source of truth for every audit action across services and UI.
// Values are lowercase snake_case, matching the DB enum and UI i18n keys.

export const AuditActions = {
    ROLE_ASSIGNED:      'role_assigned',
    ROLE_REVOKED:       'role_revoked',
    ROLE_REPLACED:      'role_replaced',
    USER_INVITED:       'user_invited',
    USER_ACTIVATED:     'user_activated',
    USER_UPDATED:       'user_updated',
    ACCOUNT_SUSPENDED:  'account_suspended',
    ACCOUNT_REACTIVATED: 'account_reactivated',
    ORG_CREATED:        'org_created',
    ORG_UPDATED:        'org_updated',
    ORG_DEACTIVATED:    'org_deactivated',
    ORG_REACTIVATED:    'org_reactivated',
    SETTINGS_IMPORTED:  'settings_imported',
    PERM_CHANGED:       'perm_changed',
    ROLE_CREATED:       'role_created',
    ROLE_UPDATED:       'role_updated',
    ROLE_DELETED:       'role_deleted',
    ROLE_RESTORED:      'role_restored',
    INVITE_CREATED:     'invite_created',
    INVITE_REGENERATED: 'invite_regenerated',
    INVITE_RESENT:      'invite_resent',
    INVITE_REVOKED:     'invite_revoked',
    LOGIN:              'login',
    MFA_ENABLED:        'mfa_enabled',
    MFA_DISABLED:       'mfa_disabled',
    OPS_SETTINGS_UPDATED: 'ops_settings_updated',
} as const;

/**
 * The event name carried by an admin-ops settings audit row. Not a Pub/Sub
 * event: nothing publishes it and nothing subscribes. `audit_logs.event_name`
 * needs a value and the mapper keys on one, so it is declared beside the action
 * it maps to rather than in the event registry, where every other name is a
 * topic something listens on.
 */
export const OPS_SETTINGS_UPDATED_EVENT = 'ADMIN.OPS_SETTINGS_UPDATED';

export type AuditActionValue = (typeof AuditActions)[keyof typeof AuditActions];

export const AuditActionEnum = z.enum([
    'role_assigned',
    'role_revoked',
    'role_replaced',
    'user_invited',
    'user_activated',
    'user_updated',
    'account_suspended',
    'account_reactivated',
    'org_created',
    'org_updated',
    'org_deactivated',
    'org_reactivated',
    'settings_imported',
    'perm_changed',
    'role_created',
    'role_updated',
    'role_deleted',
    'role_restored',
    'invite_created',
    'invite_regenerated',
    'invite_resent',
    'invite_revoked',
    'login',
    'mfa_enabled',
    'mfa_disabled',
    'ops_settings_updated',
]);

// ─── Audit Target Types ─────────────────────────────────────────────

export const AuditTargetTypes = {
    USER:   'USER',
    ACCESS: 'ACCESS',
    TENANT: 'TENANT',
    INVITE: 'INVITE',
    SYSTEM: 'SYSTEM',
} as const;

export type AuditTargetTypeValue = (typeof AuditTargetTypes)[keyof typeof AuditTargetTypes];

export const AuditTargetTypeEnum = z.enum(['USER', 'ACCESS', 'TENANT', 'INVITE', 'SYSTEM']);

// ─── Category → Actions map ─────────────────────────────────────────
// Shared between frontend and backend for consistent category filtering.

export const AUDIT_CATEGORY_ACTIONS: Record<
    'access' | 'identity' | 'orgs' | 'auth',
    AuditActionValue[]
> = {
    access:   ['role_assigned', 'role_revoked', 'role_replaced', 'perm_changed', 'role_created', 'role_updated', 'role_deleted', 'role_restored'],
    identity: ['user_invited', 'user_activated', 'user_updated', 'account_suspended', 'account_reactivated', 'invite_created', 'invite_regenerated', 'invite_resent', 'invite_revoked'],
    orgs:     ['org_created', 'org_updated', 'org_deactivated', 'org_reactivated'],
    auth:     ['login', 'mfa_enabled', 'mfa_disabled'],
};

export type AuditCategory = keyof typeof AUDIT_CATEGORY_ACTIONS;

// ─── Audit Log DTO ──────────────────────────────────────────────────
// Shape returned by the BFF — consumed by the frontend.

export const AuditLogDtoSchema = z.object({
    id:              z.string(),
    tenantId:        z.string().nullable(),
    actorUserId:     z.string().nullable(),
    actorEmail:      z.string().nullable(),
    actorDisplayName:z.string().nullable(),
    action:          AuditActionEnum,
    targetType:      AuditTargetTypeEnum,
    targetId:        z.string(),
    targetName:      z.string().nullable(),
    targetEmail:     z.string().nullable(),
    eventName:       z.string(),
    correlationId:   z.string().nullable(),
    ip:              z.string().nullable(),
    userAgent:       z.string().nullable(),
    metadata:        z.record(z.unknown()),
    createdAt:       z.string(), // ISO string from JSON serialisation
});

export type AuditLogDto = z.infer<typeof AuditLogDtoSchema>;
